import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { CoreActions } from '@next/core-lib';
import { UserActions } from '@next/core-lib/user';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin } from 'rxjs';
import { switchMap, map, startWith, finalize, withLatestFrom, filter } from 'rxjs/operators';

import { SetDefaultCropYear } from 'src/app/store/actions';
import { getCropYear } from 'src/app/store/selectors';

import * as CropAidActions from './crop-aid.actions';
import { SiteState } from '../../shared/models';
import { UnitOfMeasurementEnum } from '../crop-aid.model';
import { CropAidApiService } from '../services/crop-aid-api.service';

@Injectable()
export class CropAidEffects {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly cropAidapi = inject(CropAidApiService);
  private readonly platformID = inject<Object>(PLATFORM_ID);

  customerInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetDefaultCropYear, UserActions.customerChanged),
      filter(() => isPlatformBrowser(this.platformID)),
      map(() => CropAidActions.getCropAids()),
    ),
  );

  getCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CropAidActions.getCropAids),
      withLatestFrom(this.store.select(getCropYear)),
      switchMap(([_, cropYear]) =>
        forkJoin([
          this.cropAidapi.getCropAids(),
          this.cropAidapi.getProprietaryCropAids(cropYear),
        ]).pipe(
          map(([cropAids, proprietaryCropAids]) => {
            const mapped = [
              ...proprietaryCropAids.sort((a, b) => {
                if (a.name.toLowerCase() < b.name.toLowerCase()) {
                  return -1;
                }

                if (a.name.toLowerCase() > b.name.toLowerCase()) {
                  return 1;
                }

                return 0;
              }),
              ...cropAids.sort((a, b) => {
                if (a.name.toLowerCase() < b.name.toLowerCase()) {
                  return -1;
                }

                if (a.name.toLowerCase() > b.name.toLowerCase()) {
                  return 1;
                }

                return 0;
              }),
            ].map((cropAid) => ({
              ...cropAid,
              /* eslint-disable-next-line */
              unitOfMeasurement: (<any>UnitOfMeasurementEnum)[cropAid.unitOfMeasurement],
            }));
            return CropAidActions.setCropAids({ cropAids: mapped });
          }),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );
}
